import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [lastActivity, setLastActivity] = useState(Date.now());

  const CART_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
  const CART_STORAGE_KEY = 'properteck_cart';
  const ACTIVITY_STORAGE_KEY = 'properteck_cart_activity';

  // Update activity timestamp
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
  }, []);

  // Silent cart reset function
  const resetCart = useCallback(() => {
    setCartItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    setLastActivity(Date.now());
  }, []);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      const savedActivity = localStorage.getItem(ACTIVITY_STORAGE_KEY);
      
      if (savedCart && savedActivity) {
        const activityTime = parseInt(savedActivity, 10);
        const now = Date.now();
        
        // Check if cart has expired
        if (now - activityTime > CART_EXPIRY_TIME) {
          // Cart expired, silently reset
          resetCart();
        } else {
          // Cart still valid, restore it
          const parsedCart = JSON.parse(savedCart);
          setCartItems(parsedCart);
          setLastActivity(activityTime);
        }
      } else {
        // No saved cart or activity, set current time as activity
        updateActivity();
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      resetCart();
    }
  }, [resetCart, updateActivity]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cartItems.length > 0) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
      updateActivity();
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [cartItems, updateActivity]);

  // Set up interval to check for expiry
  useEffect(() => {
    const checkExpiry = () => {
      const now = Date.now();
      if (cartItems.length > 0 && now - lastActivity > CART_EXPIRY_TIME) {
        // Cart has expired, silently reset
        resetCart();
      }
    };

    // Check every minute
    const intervalId = setInterval(checkExpiry, 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [cartItems.length, lastActivity, resetCart]);

  const addToCart = (item) => {
    setCartItems((prevItems) => {
      // Check if item already exists in cart
      const existingItem = prevItems.find((i) => i.id === item.id);
      
      if (existingItem) {
        // If item exists, increase quantity
        return prevItems.map((i) => 
          i.id === item.id 
            ? { ...i, quantity: (i.quantity || 1) + 1 } 
            : i
        );
      } else {
        // If item doesn't exist, add it with quantity 1
        return [...prevItems, { ...item, quantity: 1 }];
      }
    });
    updateActivity();
  };

  const removeFromCart = (id) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
    updateActivity();
  };

  const updateQuantity = (id, change) => {
    setCartItems((prevItems) => 
      prevItems.map((item) => {
        if (item.id === id) {
          const newQuantity = (item.quantity || 1) + change;
          return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
        }
        return item;
      })
    );
    updateActivity();
  };

  // Added this function to expose setCartItems for direct updates
  const updateCart = (items) => {
    setCartItems(items);
    updateActivity();
  };

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      addToCart, 
      removeFromCart, 
      updateQuantity,
      updateCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);